/**
 * LensingRenderer - 引力透镜渲染器
 * @description 使用 Blackhole.ts 的光线追踪结果渲染弯曲路径，使用 Phaser Graphics API 绘制
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { Blackhole } from './Blackhole.ts';
import type { LightRayTraceResult } from './Blackhole.ts';

/** 默认渲染配置 */
const DEFAULT_LENSING_RENDER_CONFIG = {
  /** 光线路径颜色 */
  lightRayColor: 0xffd700,
  /** 被捕获光线颜色（红色） */
  capturedRayColor: 0xff4444,
  /** 逃逸光线颜色（蓝色） */
  escapedRayColor: 0x4488ff,
  /** 光线宽度 */
  lineWidth: 2,
  /** 光线数量 */
  rayCount: 24,
  /** 光线步进大小 */
  stepSize: 5,
  /** 光线最大长度 */
  maxRayLength: 500,
  /** 黑洞核心颜色 */
  blackholeCoreColor: 0x000000,
  /** 事件视界颜色 */
  eventHorizonColor: 0xff6600,
  /** 光源标记颜色 */
  lightSourceColor: 0xffff00,
  /** 光源标记半径 */
  lightSourceRadius: 15,
  /** 黑洞标记半径 */
  blackholeMarkerRadius: 30,
  /** 动画速度 */
  animationSpeed: 0.05,
  /** 坐标系 y 轴偏移 */
  offsetY: 600,
  /** 渲染缩放因子 */
  scale: 1,
};

/** 渲染配置接口 */
export interface LensingRenderConfig {
  lightRayColor?: number;
  capturedRayColor?: number;
  escapedRayColor?: number;
  lineWidth?: number;
  rayCount?: number;
  stepSize?: number;
  maxRayLength?: number;
  blackholeCoreColor?: number;
  eventHorizonColor?: number;
  lightSourceColor?: number;
  lightSourceRadius?: number;
  blackholeMarkerRadius?: number;
  animationSpeed?: number;
  offsetY?: number;
  scale?: number;
}

/** 光线路径缓存 */
interface LightPathCache {
  result: LightRayTraceResult;
  screenPath: { x: number; y: number }[];
  timestamp: number;
}

/**
 * LensingRenderer 类
 * 负责将黑洞光线追踪结果可视化绘制到 Phaser 场景
 */
export class LensingRenderer {
  private scene: Phaser.Scene;
  private blackhole: Blackhole;
  private graphics: Phaser.GameObjects.Graphics;
  private config: typeof DEFAULT_LENSING_RENDER_CONFIG;
  private pathCache: LightPathCache[];
  private lightSourcePosition: Vec2;
  private animationTime: number;
  private updateInterval: number;
  private lastUpdateTime: number;

  constructor(
    scene: Phaser.Scene,
    blackhole: Blackhole,
    config?: LensingRenderConfig,
    lightSourcePosition?: Vec2
  ) {
    this.scene = scene;
    this.blackhole = blackhole;
    this.config = { ...DEFAULT_LENSING_RENDER_CONFIG, ...config };
    this.pathCache = [];
    this.animationTime = 0;
    this.updateInterval = 100; // 缓存更新间隔（毫秒）
    this.lastUpdateTime = 0;

    // 设置光源位置（默认在黑洞左侧）
    const bhPos = blackhole.getPosition();
    this.lightSourcePosition = lightSourcePosition ?? Vec2(bhPos.x - 300, bhPos.y);

    this.graphics = scene.add.graphics();
    this.graphics.setDepth(5);

    // 初始化光线追踪
    this.initializeLightRays();
  }

  /**
   * 初始化光线追踪
   * 从光源发射多条光线
   */
  private initializeLightRays(): void {
    this.pathCache = [];

    const sourcePos = this.lightSourcePosition;
    const bhPos = this.blackhole.getPosition();
    const rayCount = this.config.rayCount;

    // 计算光源到黑洞的方向
    const dirToBlackhole = Vec2(
      bhPos.x - sourcePos.x,
      bhPos.y - sourcePos.y
    );
    const angleToBlackhole = Math.atan2(dirToBlackhole.y, dirToBlackhole.x);

    // 发射扇形分布的光线
    const spreadAngle = Math.PI * 0.6; // 扇形角度范围
    const angleStep = spreadAngle / Math.max(rayCount - 1, 1);
    const startAngle = angleToBlackhole - spreadAngle / 2;

    for (let i = 0; i < rayCount; i++) {
      const angle = startAngle + i * angleStep;
      const direction = Vec2(Math.cos(angle), Math.sin(angle));

      const result = this.blackhole.traceLightRay(sourcePos, direction, {
        stepSize: this.config.stepSize,
        maxSteps: Math.floor(this.config.maxRayLength / this.config.stepSize),
      });

      // 转换路径到屏幕坐标
      const screenPath = result.path.map(p => this.toScreenCoords(p));

      this.pathCache.push({
        result,
        screenPath,
        timestamp: 0,
      });
    }
  }

  /**
   * 更新光线追踪缓存
   */
  private updatePathCache(currentTime: number): void {
    if (currentTime - this.lastUpdateTime < this.updateInterval) {
      return;
    }

    this.lastUpdateTime = currentTime;

    // 重新追踪光线
    for (let i = 0; i < this.pathCache.length; i++) {
      const cached = this.pathCache[i];
      if (!cached) continue;
      
      const sourcePos = this.lightSourcePosition;
      const bhPos = this.blackhole.getPosition();

      // 计算光线方向（从光源出发）
      const dirToBlackhole = Vec2(
        bhPos.x - sourcePos.x,
        bhPos.y - sourcePos.y
      );
      const angleToBlackhole = Math.atan2(dirToBlackhole.y, dirToBlackhole.x);

      const spreadAngle = Math.PI * 0.6;
      const angleStep = spreadAngle / Math.max(this.config.rayCount - 1, 1);
      const startAngle = angleToBlackhole - spreadAngle / 2;

      const angle = startAngle + i * angleStep;
      const direction = Vec2(Math.cos(angle), Math.sin(angle));

      const result = this.blackhole.traceLightRay(sourcePos, direction, {
        stepSize: this.config.stepSize,
        maxSteps: Math.floor(this.config.maxRayLength / this.config.stepSize),
      });

      cached.result = result;
      cached.screenPath = result.path.map(p => this.toScreenCoords(p));
      cached.timestamp = currentTime;
    }
  }

  /**
   * 渲染所有内容
   */
  render(time?: number): void {
    this.graphics.clear();

    const currentTime = time ?? this.animationTime;
    this.updatePathCache(currentTime);

    // 渲染顺序：背景星空 -> 光线路径 -> 黑洞标记 -> 光源标记
    this.renderStarfield(currentTime);
    this.renderLightRayPaths(currentTime);
    this.renderBlackholeMarker();
    this.renderLightSourceMarker();

    // 更新动画时间
    this.animationTime += this.config.animationSpeed;
  }

  /**
   * 渲染光线路径
   */
  renderLightRayPaths(time: number): void {
    for (const cached of this.pathCache) {
      this.drawSingleLightRayPath(cached, time);
    }
  }

  /**
   * 绘制单条光线路径
   */
  private drawSingleLightRayPath(cached: LightPathCache, time: number): void {
    const { result, screenPath } = cached;

    if (!screenPath || screenPath.length < 2) {
      return;
    }

    const firstPoint = screenPath[0];
    if (!firstPoint) return;

    // 根据光线状态选择颜色
    let color: number;
    if (result.captured) {
      color = this.config.capturedRayColor;
    } else if (result.escaped) {
      color = this.config.escapedRayColor;
    } else {
      color = this.config.lightRayColor;
    }

    // 绘制路径
    this.graphics.lineStyle(this.config.lineWidth, color, 0.7);
    this.graphics.beginPath();
    this.graphics.moveTo(firstPoint.x, firstPoint.y);

    // 绘制弯曲路径
    for (let i = 1; i < screenPath.length; i++) {
      const point = screenPath[i];
      if (!point) continue;
      
      // 动画效果：路径分段渐变透明度
      const progress = i / screenPath.length;
      const alpha = 0.3 + progress * 0.5;

      // 动态发光效果
      const pulse = Math.sin(time * 3 + i * 0.1) * 0.2 + 0.8;
      this.graphics.lineStyle(this.config.lineWidth, color, alpha * pulse);

      this.graphics.lineTo(point.x, point.y);
    }

    this.graphics.strokePath();

    // 绘制路径终点标记
    const endPoint = screenPath[screenPath.length - 1];
    if (endPoint) {
      this.drawRayEndpoint(endPoint, result);
    }
  }

  /**
   * 绘制光线终点标记
   */
  private drawRayEndpoint(
    endpoint: { x: number; y: number },
    result: LightRayTraceResult
  ): void {
    const markerRadius = 5;
    let color: number;

    if (result.captured) {
      // 被捕获：绘制黑洞入口标记
      color = 0xff0000;
      this.graphics.fillStyle(color, 0.8);
      this.graphics.fillCircle(endpoint.x, endpoint.y, markerRadius);
    } else if (result.escaped) {
      // 逃逸：绘制箭头表示方向
      color = this.config.escapedRayColor;
      this.graphics.fillStyle(color, 0.6);
      this.graphics.fillCircle(endpoint.x, endpoint.y, markerRadius);
    }
  }

  /**
   * 渲染黑洞标记
   */
  renderBlackholeMarker(): void {
    const bhPos = this.blackhole.getPosition();
    const screenPos = this.toScreenCoords(bhPos);
    const rs = this.blackhole.getSchwarzschildRadius() * this.config.scale;

    // 绘制事件视界（橙红色圆环）
    this.graphics.lineStyle(3, this.config.eventHorizonColor, 0.8);
    this.graphics.strokeCircle(screenPos.x, screenPos.y, rs);

    // 绘制黑洞核心（黑色圆）
    this.graphics.fillStyle(this.config.blackholeCoreColor, 1);
    this.graphics.fillCircle(screenPos.x, screenPos.y, rs * 0.8);

    // 绘制吸积盘效果（多层光环）
    const ringCount = 3;
    for (let i = 1; i <= ringCount; i++) {
      const ringRadius = rs * (1 + i * 0.3);
      const ringAlpha = 0.5 - i * 0.1;
      const ringColor = i === 1 ? 0xff8800 : i === 2 ? 0xffaa44 : 0xffcc66;

      this.graphics.lineStyle(2, ringColor, ringAlpha);
      this.graphics.strokeCircle(screenPos.x, screenPos.y, ringRadius);
    }

    // 绘制黑洞标记
    this.graphics.fillStyle(this.config.eventHorizonColor, 0.6);
    this.graphics.fillCircle(
      screenPos.x,
      screenPos.y,
      this.config.blackholeMarkerRadius
    );

    // 绘制标签
    const label = this.scene.add.text(
      screenPos.x,
      screenPos.y - this.config.blackholeMarkerRadius - 10,
      `M=${this.blackhole.getMass()}`,
      {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
    label.setDepth(6);
  }

  /**
   * 渲染光源标记
   */
  renderLightSourceMarker(): void {
    const screenPos = this.toScreenCoords(this.lightSourcePosition);

    // 绘制光源圆
    this.graphics.fillStyle(this.config.lightSourceColor, 0.9);
    this.graphics.fillCircle(screenPos.x, screenPos.y, this.config.lightSourceRadius);

    // 绘制光源边框
    this.graphics.lineStyle(2, 0xffffff, 1);
    this.graphics.strokeCircle(screenPos.x, screenPos.y, this.config.lightSourceRadius);

    // 绘制光源标签
    const label = this.scene.add.text(
      screenPos.x,
      screenPos.y - this.config.lightSourceRadius - 10,
      '光源',
      {
        fontSize: '12px',
        color: '#ffff00',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
    label.setDepth(6);
  }

  /**
   * 渲染背景星空
   */
  renderStarfield(time: number): void {
    const starCount = 50;

    for (let i = 0; i < starCount; i++) {
      // 随机位置（伪随机，基于索引）
      const seed = i * 13.37;
      const x = (seed * 17 % 800);
      const y = (seed * 31 % 600);

      // 星星亮度闪烁
      const twinkle = Math.sin(time * 2 + seed) * 0.3 + 0.7;
      const alpha = twinkle * 0.3;

      // 绘制星星
      this.graphics.fillStyle(0xffffff, alpha);
      this.graphics.fillCircle(x, y, 1);
    }
  }

  /**
   * 绘制爱因斯坦环
   * 当光源、黑洞、观测者对齐时形成的环状图像
   */
  renderEinsteinRing(): void {
    const bhPos = this.blackhole.getPosition();
    const screenPos = this.toScreenCoords(bhPos);
    const rs = this.blackhole.getSchwarzschildRadius() * this.config.scale;

    // 计算爱因斯坦环半径（简化公式）
    const einsteinRadius = Math.sqrt(rs * 2) * this.config.scale;

    // 绘制爱因斯坦环
    this.graphics.lineStyle(4, 0xffff88, 0.4);
    this.graphics.strokeCircle(screenPos.x, screenPos.y, einsteinRadius);

    // 添加环的标签
    const label = this.scene.add.text(
      screenPos.x + einsteinRadius + 10,
      screenPos.y,
      `爱因斯坦环`,
      {
        fontSize: '10px',
        color: '#ffff88',
        fontFamily: 'Arial',
      }
    ).setOrigin(0, 0.5);
    label.setDepth(6);
  }

  /**
   * 转换物理坐标到屏幕坐标
   */
  private toScreenCoords(pos: Vec2): { x: number; y: number } {
    return {
      x: pos.x * this.config.scale,
      y: this.config.offsetY - pos.y * this.config.scale,
    };
  }

  /**
   * 设置光源位置
   */
  setLightSourcePosition(position: Vec2): void {
    this.lightSourcePosition = Vec2(position.x, position.y);
    this.initializeLightRays();
  }

  /**
   * 更新黑洞位置（实时更新）
   */
  updateBlackholePosition(position: Vec2): void {
    this.blackhole.setPosition(position);
    this.initializeLightRays();
  }

  /**
   * 更新黑洞质量
   */
  updateBlackholeMass(mass: number): void {
    this.blackhole.setMass(mass);
    this.initializeLightRays();
  }

  /**
   * 更新渲染配置
   */
  updateConfig(config: LensingRenderConfig): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 获取当前配置
   */
  getConfig(): typeof DEFAULT_LENSING_RENDER_CONFIG {
    return { ...this.config };
  }

  /**
   * 获取光线追踪结果（用于分析）
   */
  getLightRayResults(): LightRayTraceResult[] {
    return this.pathCache.map(c => c.result);
  }

  /**
   * 获取被捕获光线数量
   */
  getCapturedRayCount(): number {
    return this.pathCache.filter(c => c.result.captured).length;
  }

  /**
   * 获取逃逸光线数量
   */
  getEscapedRayCount(): number {
    return this.pathCache.filter(c => c.result.escaped).length;
  }

  /**
   * 计算平均偏折角
   */
  getAverageDeflectionAngle(): number {
    const angles = this.pathCache
      .filter(c => !c.result.captured && c.result.deflectionAngle !== Infinity)
      .map(c => c.result.deflectionAngle);

    if (angles.length === 0) return 0;

    return angles.reduce((sum, a) => sum + a, 0) / angles.length;
  }

  /**
   * 清除所有渲染
   */
  clear(): void {
    this.graphics.clear();
    this.pathCache = [];
  }

  /**
   * 销毁渲染器
   */
  destroy(): void {
    this.clear();
    this.graphics.destroy();
  }

  /**
   * 获取 Graphics 对象
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  /**
   * 获取黑洞实例
   */
  getBlackhole(): Blackhole {
    return this.blackhole;
  }

  /**
   * 获取光源位置
   */
  getLightSourcePosition(): Vec2 {
    return this.lightSourcePosition;
  }

  /**
   * 获取动画时间
   */
  getAnimationTime(): number {
    return this.animationTime;
  }
}

/**
 * 创建引力透镜渲染器
 */
export function createLensingRenderer(
  scene: Phaser.Scene,
  blackhole: Blackhole,
  config?: LensingRenderConfig,
  lightSourcePosition?: Vec2
): LensingRenderer {
  return new LensingRenderer(scene, blackhole, config, lightSourcePosition);
}